import React, { useEffect, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { analytics } from '@/services/analytics';
import { inputMethodFromEvent } from '@/services/analyticsContext';
import {
  pricingService,
  PricingConfig,
  PricingTier,
  TierId,
} from '@/services/pricingService';
import { isNativeApp } from '@/utils/platform';
import { authService } from '@/services/authService';
import PlanCard, { planButtonClasses, planStatusClasses } from './PlanCard';

// The server's effective tier -> the pricing page's id for it. `premium` is
// stored for what the page calls Scholar.
const PRICING_ID_BY_TIER: Record<string, TierId> = { free: 'free', member: 'member', premium: 'scholar' };
const TIER_ORDER: TierId[] = ['free', 'member', 'scholar'];

interface PricingTiersProps {
  // 'modal' is the compact layout used inside the usage-limit modal;
  // 'page' is the roomier standalone /pricing layout.
  variant?: 'modal' | 'page';
  // Where the user opened pricing from — sent with analytics events.
  source?: string;
}

type CaptureState = 'idle' | 'capturing' | 'done';

const formatPrice = (tier: PricingTier, symbol: string): string => {
  // "£0" rather than "Free": it lines up with the other prices, and the
  // card's own name already says Free.
  if (tier.price === 0) return `${symbol}0`;
  return `${symbol}${tier.price.toFixed(2)}`;
};

const PricingTiers: React.FC<PricingTiersProps> = ({ variant = 'page', source }) => {
  // App Store anti-steering (guideline 3.1.3(a)): the native app is a reader that
  // honours web-purchased tiers but must never show prices or upgrade UI. This is
  // the single hard gate — every pricing surface renders through PricingTiers.
  // isNativeApp() is environment-constant, so the early return keeps a stable
  // hook order between renders.
  if (isNativeApp()) return null;

  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const [config, setConfig] = useState<PricingConfig | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Which card is the visitor's, from the server's effective tier when they
  // are signed in. Until the first subscription existed this page could
  // assume Free; a Member buying in the iOS app and visiting the site was
  // the first person it was wrong for. Null means unknown or signed out,
  // and the page falls back to the config's own `cta: 'current'`.
  const [currentTier, setCurrentTier] = useState<TierId | null>(null);

  // Per-tier subscribe flow state.
  const [captureState, setCaptureState] = useState<Record<string, CaptureState>>({});
  const [emails, setEmails] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    pricingService
      .fetchPricing()
      .then((data) => {
        if (!cancelled) setConfig(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    if (authService.isAuthenticated()) {
      authService
        .getEntitlements()
        .then((entitlements) => {
          if (!cancelled) setCurrentTier(PRICING_ID_BY_TIER[entitlements.tier] ?? null);
        })
        .catch(() => {
          // Signed out meanwhile, or offline: fall back to the config's default.
        });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubscribeClick = (event: React.MouseEvent, tier: PricingTier) => {
    // Count clicks per tier (wired to Mixpanel).
    analytics.track('Pricing Upgrade Clicked', {
      tier: tier.id,
      tier_name: tier.name,
      price: tier.price,
      currency: config?.currency,
      source,
      surface: variant === 'modal' ? 'pricing_modal' : 'pricing_page',
      trigger: 'button',
      input_method: inputMethodFromEvent(event),
    });
    setCaptureState((prev) => ({ ...prev, [tier.id]: 'capturing' }));
  };

  const handleEmailSubmit = (e: React.FormEvent, tier: PricingTier) => {
    e.preventDefault();
    const email = (emails[tier.id] || '').trim();
    if (!email) return;

    // Capture the interested email via analytics (no billing yet).
    analytics.track('Pricing Waitlist Joined', {
      tier: tier.id,
      tier_name: tier.name,
      price: tier.price,
      currency: config?.currency,
      email,
      source,
      surface: variant === 'modal' ? 'pricing_modal' : 'pricing_page',
      trigger: 'button',
      input_method: inputMethodFromEvent(e),
    });
    setCaptureState((prev) => ({ ...prev, [tier.id]: 'done' }));
  };

  if (loadError) {
    return (
      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        Couldn't load pricing right now. Please try again later.
      </p>
    );
  }

  if (!config) {
    return (
      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        Loading plans…
      </p>
    );
  }

  const mutedText = isDarkMode ? 'text-black-300' : 'text-black-500';

  return (
    <div
      className={`grid grid-cols-1 ${
        variant === 'page' ? 'md:grid-cols-3 gap-6 lg:gap-8' : 'md:grid-cols-3 gap-4'
      }`}
    >
      {config.tiers.map((tier: PricingTier) => {
        const state: CaptureState = captureState[tier.id] || 'idle';
        const isCurrent = currentTier ? tier.id === currentTier : tier.cta === 'current';
        // Nothing to offer on a card below the one they have: the web has no
        // checkout, and a subscription bought in the app is managed there.
        const rank = (id: TierId) => TIER_ORDER.indexOf(id);
        const isBelowCurrent = currentTier
          ? rank(tier.id) < rank(currentTier)
          : tier.cta === 'current' && !isCurrent;

        return (
          <PlanCard
            key={tier.id}
            name={tier.name}
            tagline={tier.tagline}
            price={formatPrice(tier, config.symbol)}
            priceSuffix="/mo"
            features={tier.features}
            // Outline only a plan the server confirmed is theirs: for a signed-out
            // visitor it would just draw the eye to Free.
            highlighted={currentTier !== null && isCurrent}
            variant={variant}
            isDarkMode={isDarkMode}
          >
            {isCurrent ? (
              <div className={planStatusClasses(isDarkMode, 'accent')}>Your current plan</div>
            ) : isBelowCurrent ? (
              <div className={planStatusClasses(isDarkMode)}>Included in your plan</div>
            ) : state === 'done' ? (
              <p className={planStatusClasses(isDarkMode, 'accent')}>
                Coming soon — you're first in line.
              </p>
            ) : state === 'capturing' ? (
              <form onSubmit={(e) => handleEmailSubmit(e, tier)} className="flex flex-col gap-3">
                <p className={`text-xs leading-relaxed ${mutedText}`}>
                  We're not quite ready yet. Please, leave your email and we'll get back to you
                  as soon as we can let you in.
                </p>
                <input
                  type="email"
                  required
                  autoFocus
                  placeholder="you@email.com"
                  value={emails[tier.id] || ''}
                  onChange={(e) =>
                    setEmails((prev) => ({ ...prev, [tier.id]: e.target.value }))
                  }
                  className={`w-full rounded-full border px-4 py-3 text-sm outline-none transition-colors ${
                    isDarkMode
                      ? 'border-white/15 bg-white/5 text-white placeholder-gray-400 focus:border-blue-400'
                      : 'border-black-200 bg-true-white text-black-1500 placeholder-gray-400 focus:border-blue-700'
                  }`}
                />
                <button type="submit" className={planButtonClasses}>
                  Notify me
                </button>
              </form>
            ) : (
              <button onClick={(event) => handleSubscribeClick(event, tier)} className={planButtonClasses}>
                Upgrade
              </button>
            )}
          </PlanCard>
        );
      })}
    </div>
  );
};

export default PricingTiers;
